import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, getServerConfig } from "@/lib/api";
import { toast } from "sonner";
import { AppWindow, Plus, Globe, Copy, ArrowRight, Code, ShieldCheck } from "@phosphor-icons/react";

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);

  const refresh = () => {
    setLoading(true);
    api.get("/applications").then(r => setApps(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); getServerConfig().then(setCfg).catch(() => {}); }, []);
  const isLive = cfg?.razorpay_mode === "live";

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Partners › OAuth</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-[#0F172A] mt-1">Applications</h1>
          <p className="mt-1.5 text-sm text-slate-500 max-w-xl">An application is your registered identity on Razorpay's OAuth. Each app has a development and production client.</p>
        </div>
        {!isLive && (
          <button onClick={() => setShowCreate(true)} data-testid="create-application-button"
            className="btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium">
            <Plus size={14} weight="bold" /> Create Application
          </button>
        )}
      </div>

      {isLive && (
        <div className="rounded-xl border border-[#D1FAE5] bg-[#D1FAE5]/30 p-4 flex items-start gap-3" data-testid="live-mode-banner">
          <ShieldCheck size={20} weight="duotone" className="text-[#10B981] mt-0.5 flex-shrink-0" />
          <div className="text-xs text-[#065F46]">
            <div className="font-semibold">Live mode active</div>
            <div className="mt-1 text-[#065F46]/80">Applications are managed via <code className="font-mono">/app/backend/.env</code> and seeded via <code className="font-mono">scripts/seed_razorpay_app.py</code>. The "Create Application" UI is disabled to prevent accidental mismatches with Razorpay's registered apps.</div>
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonCards />
      ) : apps.length === 0 ? (
        <EmptyApps onCreate={() => setShowCreate(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="applications-grid">
          {apps.map(a => <AppCard key={a.id} app={a} />)}
        </div>
      )}

      {showCreate && <CreateAppModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
    </div>
  );
}

function AppCard({ app }) {
  return (
    <Link to={`/applications/${app.id}`} className="group rounded-xl border border-[#E2E8F0] bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-[#FF6B00]/30" data-testid={`app-card-${app.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#FFF3E0] text-[#FF6B00]">
            <AppWindow size={18} weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-[#0F172A] truncate">{app.name}</div>
            <div className="text-xs text-slate-500 truncate flex items-center gap-1"><Globe size={11}/>{app.website}</div>
          </div>
        </div>
        <ArrowRight size={16} className="text-slate-300 transition-transform group-hover:-rotate-45 group-hover:text-[#FF6B00]" />
      </div>

      <div className="mt-5 space-y-2">
        {app.clients?.map(c => (
          <div key={c.client_id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${c.env === "production" ? "text-[#10B981]" : "text-[#FF6B00]"}`}>
              {c.env}
            </span>
            <code className="font-mono text-xs text-slate-700 truncate ml-3" data-testid={`app-client-id-${c.client_id}`}>{c.client_id}</code>
          </div>
        ))}
      </div>
    </Link>
  );
}

function EmptyApps({ onCreate }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-applications">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF3E0]">
        <Code size={24} className="text-[#FF6B00]" weight="duotone" />
      </div>
      <h3 className="font-display text-lg font-semibold text-[#0F172A]">No applications yet</h3>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-slate-500">
        Create your first OAuth application to receive a development and production client. Only Owners can create applications.
      </p>
      <button onClick={onCreate} data-testid="empty-create-application-button"
        className="btn-primary mt-5 inline-flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium">
        <Plus size={14} weight="bold" /> Create your first application
      </button>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {[0,1,2].map(i => (
        <div key={i} className="rounded-xl border border-[#E2E8F0] bg-white p-5 animate-pulse">
          <div className="h-10 w-10 rounded-md bg-slate-100" />
          <div className="mt-3 h-4 w-2/3 rounded bg-slate-100" />
          <div className="mt-2 h-3 w-1/2 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function CreateAppModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [redirectUri, setRedirectUri] = useState(`${window.location.origin}/oauth/callback`);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const uris = redirectUri.split(",").map(s => s.trim()).filter(Boolean);
      await api.post("/applications", {
        name, website, redirect_uris: uris, webhook_url: webhookUrl || null,
      });
      toast.success("Application created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl bg-white shadow-xl" data-testid="create-app-modal">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h3 className="font-display text-lg font-semibold">Create Application</h3>
          <p className="text-xs text-slate-500 mt-0.5">We'll auto-generate a development and production client for you.</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <Field label="Application name" required>
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="create-app-name"
              placeholder="Acme Payments" className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
          </Field>
          <Field label="Website" required>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} required data-testid="create-app-website"
              placeholder="https://acme.com" className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
          </Field>
          <Field label="Redirect URI(s) — comma separated" required>
            <input value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} required data-testid="create-app-redirect"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15 font-mono text-xs" />
            <p className="mt-1 text-[11px] text-slate-500">Production clients require <code>https://</code> URIs.</p>
          </Field>
          <Field label="Webhook URL (optional)">
            <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} data-testid="create-app-webhook"
              placeholder="https://acme.com/webhooks/razorpay" className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15 font-mono text-xs" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#E2E8F0] px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100" data-testid="create-app-cancel">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="create-app-submit">
            {submitting ? "Creating…" : "Create application"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label} {required && <span className="text-[#FF6B00]">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function CopyButton({ value, testId, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" data-testid={testId}
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success(`${label || "Copied"}`); }}
      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
      <Copy size={12} /> {copied ? "Copied" : "Copy"}
    </button>
  );
}
